import React, { useEffect, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MetricChart } from '../components/dashboard/MetricChart';
import { ListWidget } from '../components/dashboard/ListWidget';
import { CompanySelector, Company } from '../components/dashboard/CompanySelector';
import { DateRangePicker } from '../components/dashboard/DateRangePicker';
import { GranularitySelector, Granularity, getAvailableGranularities, getDefaultGranularity } from '../components/dashboard/GranularitySelector';
import { ComparisonSelector, ComparisonType } from '../components/dashboard/ComparisonSelector';
import { PaymentMethodFilter, PaymentMethod } from '../components/dashboard/PaymentMethodFilter';
import { AddChartModal } from '../components/dashboard/AddChartModal';
import { EditChartsModal } from '../components/dashboard/EditChartsModal';
import { AnalyticsSection } from '../components/dashboard/AnalyticsWidgets';
import { InfoTooltip } from '../components/InfoTooltip';
import { BalanceSnapshotWidget } from '../components/dashboard/BalanceSnapshotWidget';
import { AttentionNeededWidget } from '../components/dashboard/AttentionNeededWidget';
import { RecommendationsWidget } from '../components/dashboard/RecommendationsWidget';
import { CreditCardIcon, ArrowUpRightIcon, ArrowDownRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, FilterIcon, RotateCcwIcon, ReceiptIcon, RefreshCcwIcon, AlertTriangleIcon, WalletIcon, ChevronRightIcon, ActivityIcon, ServerIcon, LinkIcon, FileTextIcon, CalendarIcon, BellIcon } from 'lucide-react';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData, generateDisputeCountData, generatePreviousDisputeCountData, generateDisputeActivityData, generatePreviousDisputeActivityData, generateRefundRateData, generatePreviousRefundRateData, generateNewCustomersData, generatePreviousNewCustomersData, generateBlockedPaymentsData, generatePreviousBlockedPaymentsData, generateBlockedPaymentsVolumeData, generatePreviousBlockedPaymentsVolumeData, generatePreviousAverageTransactionData } from '../components/dashboard/DataGenerator';
import { mockBills } from '../utils/mockBills';
type TabType = 'dashboard' | 'actions';
export function Overview() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  // --- Analytics State ---
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [showAddChartModal, setShowAddChartModal] = useState(false);
  const [showEditChartModal, setShowEditChartModal] = useState(false);
  const [chartOrder, setChartOrder] = useState<string[]>(['gross-volume', 'net-volume', 'successful-payments', 'payment-success-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'top-countries', 'blocked-payments', 'blocked-payments-volume']);
  const [visibleCharts, setVisibleCharts] = useState<Set<string>>(new Set(['gross-volume', 'net-volume', 'successful-payments', 'payment-success-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'top-countries', 'blocked-payments', 'blocked-payments-volume']));
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7)),
    end: new Date()
  });
  const [granularity, setGranularity] = useState<Granularity>('daily');
  // Compute available granularities based on selected date range
  const availableGranularities = getAvailableGranularities(dateRange);
  const isGranularityHidden = availableGranularities.length <= 1;
  // When date range changes, auto-adjust granularity if current selection is invalid
  const handleDateRangeChange = (newRange: {
    start: Date;
    end: Date;
  }) => {
    setDateRange(newRange);
    const newAvailable = getAvailableGranularities(newRange);
    if (!newAvailable.includes(granularity)) {
      setGranularity(getDefaultGranularity(newRange));
    }
  };
  // Handle granularity change (no longer syncs date range — date range drives granularity options)
  const handleGranularityChange = (newGranularity: Granularity) => {
    setGranularity(newGranularity);
  };
  const [comparison, setComparison] = useState<ComparisonType>('previous_period');
  const [customComparisonRange, setCustomComparisonRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Chart Data State
  const [paymentVolumeData, setPaymentVolumeData] = useState<any[]>([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState<any[]>([]);
  const [transactionCountData, setTransactionCountData] = useState<any[]>([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState<any[]>([]);
  const [averageTransactionData, setAverageTransactionData] = useState<any[]>([]);
  const [previousAverageTransactionData, setPreviousAverageTransactionData] = useState<any[]>([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState<any[]>([]);
  const [geoPaymentData, setGeoPaymentData] = useState<any[]>([]);
  const [refundData, setRefundData] = useState<any[]>([]);
  const [disputeActivityData, setDisputeActivityData] = useState<any[]>([]);
  const [refundRateData, setRefundRateData] = useState<any[]>([]);
  const [previousRefundRateData, setPreviousRefundRateData] = useState<any[]>([]);
  const [newCustomersData, setNewCustomersData] = useState<any[]>([]);
  const [previousNewCustomersData, setPreviousNewCustomersData] = useState<any[]>([]);
  const [disputeCountData, setDisputeCountData] = useState<any[]>([]);
  const [previousDisputeCountData, setPreviousDisputeCountData] = useState<any[]>([]);
  const [blockedPaymentsData, setBlockedPaymentsData] = useState<any[]>([]);
  const [previousBlockedPaymentsData, setPreviousBlockedPaymentsData] = useState<any[]>([]);
  const [blockedPaymentsVolumeData, setBlockedPaymentsVolumeData] = useState<any[]>([]);
  const [previousBlockedPaymentsVolumeData, setPreviousBlockedPaymentsVolumeData] = useState<any[]>([]);
  // --- Load Companies & Portal ---
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    setSelectedCompanies(companiesData.length > 0 ? [companiesData[0]] : []);
  }, []);
  useEffect(() => {
    const sidebarContainer = document.getElementById('sidebar-company-selector');
    if (sidebarContainer && companies.length > 0) {
      ReactDOM.render(<CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-9657" />, sidebarContainer);
    }
    return () => {
      if (sidebarContainer) {
        ReactDOM.unmountComponentAtNode(sidebarContainer);
      }
    };
  }, [companies, selectedCompanies]);
  // --- Generate Data ---
  useEffect(() => {
    const startDate = dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start);
    const endDate = dateRange.end instanceof Date ? dateRange.end : new Date(dateRange.end);
    const rawPaymentVolumeData = generatePaymentVolumeData('', startDate, endDate, granularity);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, '');
    const rawTransactionCountData = generateTransactionCountData('', startDate, endDate, granularity);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, '');
    const rawAverageTransactionData = generateAverageTransactionData('', startDate, endDate, granularity);
    const rawPreviousAverageTransactionData = generatePreviousAverageTransactionData(rawAverageTransactionData, '');
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData('', startDate, endDate, granularity);
    const rawDisputeActivityData = generateDisputeActivityData('', startDate, endDate, granularity);
    const rawRefundRateData = generateRefundRateData('', startDate, endDate, granularity);
    const rawPreviousRefundRateData = generatePreviousRefundRateData(rawRefundRateData, '');
    const rawNewCustomersData = generateNewCustomersData('', startDate, endDate, granularity);
    const rawPreviousNewCustomersData = generatePreviousNewCustomersData(rawNewCustomersData, '');
    const rawDisputeCountData = generateDisputeCountData('', startDate, endDate, granularity);
    const rawPreviousDisputeCountData = generatePreviousDisputeCountData(rawDisputeCountData, '');
    const rawBlockedPaymentsData = generateBlockedPaymentsData('', startDate, endDate, granularity);
    const rawPreviousBlockedPaymentsData = generatePreviousBlockedPaymentsData(rawBlockedPaymentsData, '');
    const rawBlockedPaymentsVolumeData = generateBlockedPaymentsVolumeData('', startDate, endDate, granularity);
    const rawPreviousBlockedPaymentsVolumeData = generatePreviousBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData, '');
    const selectedCompanyIds = selectedCompanies.map(c => c.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length || selectedCompanyIds.length === 0;
    if (isAllCompanies) {
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPreviousAverageTransactionData(rawPreviousAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setDisputeActivityData(rawDisputeActivityData);
      setRefundRateData(rawRefundRateData);
      setPreviousRefundRateData(rawPreviousRefundRateData);
      setNewCustomersData(rawNewCustomersData);
      setPreviousNewCustomersData(rawPreviousNewCustomersData);
      setDisputeCountData(rawDisputeCountData);
      setPreviousDisputeCountData(rawPreviousDisputeCountData);
      setBlockedPaymentsData(rawBlockedPaymentsData);
      setPreviousBlockedPaymentsData(rawPreviousBlockedPaymentsData);
      setBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData);
      setPreviousBlockedPaymentsVolumeData(rawPreviousBlockedPaymentsVolumeData);
    } else {
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPreviousAverageTransactionData(filterDataByCompanies(rawPreviousAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData);
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setDisputeActivityData(filterDataByCompanies(rawDisputeActivityData, selectedCompanyIds));
      setRefundRateData(filterDataByCompanies(rawRefundRateData, selectedCompanyIds));
      setPreviousRefundRateData(filterDataByCompanies(rawPreviousRefundRateData, selectedCompanyIds));
      setNewCustomersData(filterDataByCompanies(rawNewCustomersData, selectedCompanyIds));
      setPreviousNewCustomersData(filterDataByCompanies(rawPreviousNewCustomersData, selectedCompanyIds));
      setDisputeCountData(filterDataByCompanies(rawDisputeCountData, selectedCompanyIds));
      setPreviousDisputeCountData(filterDataByCompanies(rawPreviousDisputeCountData, selectedCompanyIds));
      setBlockedPaymentsData(filterDataByCompanies(rawBlockedPaymentsData, selectedCompanyIds));
      setPreviousBlockedPaymentsData(filterDataByCompanies(rawPreviousBlockedPaymentsData, selectedCompanyIds));
      setBlockedPaymentsVolumeData(filterDataByCompanies(rawBlockedPaymentsVolumeData, selectedCompanyIds));
      setPreviousBlockedPaymentsVolumeData(filterDataByCompanies(rawPreviousBlockedPaymentsVolumeData, selectedCompanyIds));
    }
  }, [selectedCompanies, companies, dateRange, granularity, comparison, paymentMethods]);
  // --- Metrics Calculations ---
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  const disputeRate = disputeActivityData.length > 0 ? disputeActivityData[disputeActivityData.length - 1].rate : 0;
  const netVolume = totalVolume * 0.979; // net after fees
  const paymentSuccessRate = totalTransactions > 0 ? 72.0 : 0;
  const totalDisputeCount = disputeCountData.reduce((sum, item) => sum + item.count, 0);
  const totalBlockedCount = blockedPaymentsData.reduce((sum, item) => sum + item.count, 0);
  const totalBlockedVolume = blockedPaymentsVolumeData.reduce((sum, item) => sum + item.amount, 0);
  const spendPerCustomer = totalVolume > 0 ? totalVolume / 287 : 0;
  const calculateGrowth = (current: number, previous: number) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(2);
  };
  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `THB ${(value / 1000000).toFixed(2)}Mn`;
    if (value >= 1000) return `THB ${(value / 1000).toFixed(2)}K`;
    return `THB ${value.toFixed(2)}`;
  };
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(0)}Mn`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    if (value > 0 && value < 1) return value.toFixed(1);
    return value.toString();
  };
  const formatDate = (date: string, gran: string) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    if (gran === 'hourly') return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    if (gran === 'quarterly') return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    if (gran === 'yearly') return d.getFullYear().toString();
    if (gran === 'monthly') return d.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
    if (gran === 'weekly') return `W${Math.ceil(d.getDate() / 7)} ${d.toLocaleDateString('en-US', {
      month: 'short'
    })}`;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const getUpdateTime = () => {
    const minutes = Math.floor(Math.random() * 10) + 1;
    return `Updated ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  };
  // --- Actions & Alerts Data ---
  const pendingBills = mockBills.filter(b => b.status === 'pending_approval');
  const upcomingBills = [...mockBills].filter(b => b.status === 'approved' || b.status === 'pending_approval').sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()).slice(0, 5);
  const recentActivity = [{
    id: 1,
    type: 'payment',
    title: 'Payment received',
    desc: 'THB 45,000 from John Doe',
    time: '10 mins ago',
    icon: DollarSignIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 2,
    type: 'bill',
    title: 'Bill approved',
    desc: 'INV-2026-003 by Simon Verraest',
    time: '1 hour ago',
    icon: CheckCircleIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 3,
    type: 'dispute',
    title: 'New dispute opened',
    desc: 'THB 1,538.94 - Fraudulent',
    time: '3 hours ago',
    icon: AlertTriangleIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 4,
    type: 'refund',
    title: 'Refund processed',
    desc: 'THB 2,500 to Sarah Smith',
    time: '5 hours ago',
    icon: RotateCcwIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 5,
    type: 'payment',
    title: 'Payment received',
    desc: 'THB 12,400 from Acme Corp',
    time: 'Yesterday',
    icon: DollarSignIcon,
    color: 'text-gray-600 bg-gray-100'
  }];
  // --- Render Chart Helper ---
  const renderChart = (chartId: string) => {
    switch (chartId) {
      case 'gross-volume':
        return <MetricChart key={chartId} title="Gross volume" tooltipText="Estimated amount for captured payments, shown in your default settlement currency using average exchange rates over the selected time period. Gross sales figures include payments that were later disputed or refunded." currentValue={formatCurrency(totalVolume)} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(totalVolume, totalVolume * 0.92)) : undefined} previousValue={comparison !== 'none' ? formatCurrency(totalVolume * 0.92) : undefined} data={paymentVolumeData} previousData={comparison !== 'none' ? previousPaymentVolumeData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `THB ${value.toLocaleString('en-US')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9658" />;
      case 'successful-payments':
        return <MetricChart key={chartId} title="Successful payments" tooltipText="The number of successful payments over the given time period." currentValue={totalTransactions.toLocaleString()} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(totalTransactions, totalTransactions * 0.94)) : undefined} previousValue={comparison !== 'none' ? (totalTransactions * 0.94).toLocaleString() : undefined} data={transactionCountData} previousData={comparison !== 'none' ? previousTransactionCountData : []} dataKey="count" tooltipValueFormatter={value => `${value.toLocaleString()}`} tooltipLabel="payments" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9659" />;
      case 'refund-rate':
        return <MetricChart key={chartId} title="Refund rate" tooltipText="Refund rate represents the total number of payments that have partial and full refunds divided by the total number of successful payments in the same period." currentValue={`${refundPercentage.toFixed(1)}%`} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(refundPercentage, refundPercentage * 1.05)) : undefined} previousValue={comparison !== 'none' ? `${(refundPercentage * 1.05).toFixed(1)}%` : undefined} data={refundRateData} previousData={comparison !== 'none' ? previousRefundRateData : []} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9660" />;
      case 'payment-methods':
        return <div key={chartId} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-9661">
            <div className="flex items-center mb-6" data-id="element-9662">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-9663">
                Top payment method categories
              </h3>
              <InfoTooltip content="Estimated gross volume is the total sales volume per payment method. This includes refunds and disputes." data-id="element-9664" />
            </div>
            <div className="mb-6" data-id="element-9665">
              <div className="h-4 flex rounded-full overflow-hidden" data-id="element-9666">
                <div style={{
                width: '45%',
                backgroundColor: '#A78BFA'
              }} data-id="element-9667"></div>
                <div style={{
                width: '34%',
                backgroundColor: '#93C5FD'
              }} data-id="element-9668"></div>
                <div style={{
                width: '12%',
                backgroundColor: '#F472B6'
              }} data-id="element-9669"></div>
                <div style={{
                width: '5%',
                backgroundColor: '#FBBF24'
              }} data-id="element-9670"></div>
                <div style={{
                width: '4%',
                backgroundColor: '#92400E'
              }} data-id="element-9671"></div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-9672">
              <div className="flex justify-between items-center" data-id="element-9673">
                <div className="flex items-center gap-2" data-id="element-9674">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#A78BFA'
                }} data-id="element-9675"></div>
                  <span className="text-sm text-gray-600" data-id="element-9676">Card</span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-9677">
                  THB 240.90K
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-9678">
                <div className="flex items-center gap-2" data-id="element-9679">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#93C5FD'
                }} data-id="element-9680"></div>
                  <span className="text-sm text-gray-600" data-id="element-9681">Bank transfer</span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-9682">
                  THB 180.50K
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-9683">
                <div className="flex items-center gap-2" data-id="element-9684">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#F472B6'
                }} data-id="element-9685"></div>
                  <span className="text-sm text-gray-600" data-id="element-9686">E-wallet</span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-9687">
                  THB 95.20K
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-9688">
                <div className="flex items-center gap-2" data-id="element-9689">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#FBBF24'
                }} data-id="element-9690"></div>
                  <span className="text-sm text-gray-600" data-id="element-9691">Crypto</span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-9692">
                  THB 12.30K
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-9693">
                <div className="flex items-center gap-2" data-id="element-9694">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#92400E'
                }} data-id="element-9695"></div>
                  <span className="text-sm text-gray-600" data-id="element-9696">Other</span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-9697">
                  THB 5.10K
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center mt-5 pt-3 border-t border-gray-100" data-id="element-9698">
              <span className="text-xs text-gray-400" data-id="element-9699">{getUpdateTime()}</span>
              <button onClick={() => navigate('/payment-analytics?tab=payment-methods')} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-9700">
                View more
              </button>
            </div>
          </div>;
      case 'new-customers':
        return <MetricChart key={chartId} title="New customers" tooltipText="Number of new customers created, including customers that are subsequently deleted. This number does not include guest customers." currentValue="287" growthPercent={15.2} previousValue="249" data={newCustomersData} previousData={previousNewCustomersData} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="customers" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9701" />;
      case 'net-volume':
        return <MetricChart key={chartId} title="Net volume" tooltipText="Estimated volume from payments that are settled to your balance after fees, refunds, disputes, and transfers have been deducted." currentValue={formatCurrency(netVolume)} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(netVolume, netVolume * 0.935)) : undefined} previousValue={comparison !== 'none' ? formatCurrency(netVolume * 0.935) : undefined} data={paymentVolumeData.map(d => ({
          ...d,
          amount: d.amount * 0.979
        }))} previousData={comparison !== 'none' ? previousPaymentVolumeData.map(d => ({
          ...d,
          amount: d.amount * 0.979
        })) : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `THB ${value.toLocaleString('en-US')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9702" />;
      case 'payment-success-rate':
        return <MetricChart key={chartId} title="Payment success rate" tooltipText="The payment success rate is a measure of how many payment attempts are successfully accepted by payment providers, such as card issuers or local payment methods." currentValue={`${paymentSuccessRate.toFixed(1)}%`} growthPercent={comparison !== 'none' ? 6.17 : undefined} previousValue={comparison !== 'none' ? '69.1%' : undefined} data={transactionCountData.map(d => ({
          ...d,
          rate: 65 + Math.random() * 15
        }))} previousData={[]} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value.toFixed(1)}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9703" />;
      case 'refunds-volume':
        return <MetricChart key={chartId} title="Refunds volume" tooltipText="Amount of refunds conducted over the selected time period. The total reflects an estimated amount converted into your default settlement currency using average exchange rates over the time period." currentValue={formatCurrency(totalRefundAmount)} growthPercent={comparison !== 'none' ? -9.09 : undefined} previousValue={comparison !== 'none' ? formatCurrency(totalRefundAmount * 1.1) : undefined} data={refundData} previousData={[]} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `THB ${value.toLocaleString('en-US')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9704" />;
      case 'total-payments':
        return <ListWidget key={chartId} title="Total payments" tooltipText="Snapshot of the latest status for all payment intents created in the selected time period." items={[{
          label: 'Succeeded',
          value: '48,490'
        }, {
          label: 'Authorized',
          value: '3,109'
        }, {
          label: 'Failed',
          value: '2,160'
        }, {
          label: 'Refunded & Partially refunded',
          value: '11,094'
        }, {
          label: 'Refund failed',
          value: '114'
        }]} updateTime={getUpdateTime()} data-id="element-9705" />;
      case 'dispute-count':
        return <MetricChart key={chartId} title="Dispute count" tooltipText="Estimated number of disputes. RFIs and pre-chargebacks are not included" currentValue={totalDisputeCount.toString()} growthPercent={comparison !== 'none' ? 12.5 : undefined} previousValue={comparison !== 'none' ? '37' : undefined} data={disputeCountData} previousData={comparison !== 'none' ? previousDisputeCountData : []} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="disputes" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9706" />;
      case 'dispute-activity':
        return <MetricChart key={chartId} title="Dispute activity" tooltipText="Estimated number of disputes divided by the number of successful payments in the same time series. RFIs and pre-chargebacks are not included in your dispute rate." currentValue={`${disputeRate.toFixed(1)}%`} growthPercent={comparison !== 'none' ? -8.3 : undefined} previousValue={comparison !== 'none' ? `${(disputeRate * 1.09).toFixed(1)}%` : undefined} data={disputeActivityData} previousData={[]} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9707" />;
      case 'disputed-payments':
        return <ListWidget key={chartId} title="Disputed payments" tooltipText="Recent disputed transactions." items={[{
          label: 'THB 30.50 · Fraudulent',
          value: 'Requires response',
          valueColor: 'text-orange-600'
        }, {
          label: 'THB 122.25 · Duplicate',
          value: 'Challenged',
          valueColor: 'text-blue-600'
        }, {
          label: 'THB 16.00 · Fraudulent',
          value: 'Lost',
          valueColor: 'text-red-600'
        }]} updateTime={getUpdateTime()} data-id="element-9708" />;
      case 'failed-payments':
        return <ListWidget key={chartId} title="Failed payments" tooltipText="Most recent payment failures excluding blocked payments." items={[{
          label: 'THB 46.00 · Nov 1, 2:15 PM',
          value: 'Failed',
          valueColor: 'text-red-600'
        }, {
          label: 'THB 24.60 · Aug 3, 2:38 AM',
          value: 'Failed',
          valueColor: 'text-red-600'
        }]} updateTime={getUpdateTime()} data-id="element-9709" />;
      case 'spend-per-customer':
        return <MetricChart key={chartId} title="Spend per customer" tooltipText="Estimated gross volume for all payments created with a customer, divided by the total number of customers with payments." currentValue={formatCurrency(spendPerCustomer)} growthPercent={comparison !== 'none' ? 5.8 : undefined} previousValue={comparison !== 'none' ? formatCurrency(spendPerCustomer * 0.945) : undefined} data={averageTransactionData} previousData={comparison !== 'none' ? previousAverageTransactionData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `THB ${value.toLocaleString('en-US')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9710" />;
      case 'top-customers':
        return <ListWidget key={chartId} title="Top customers by spend" tooltipText="Estimated gross volume is the total spend per customer, including guest customers. The numbers do not include refunds and disputes." items={[{
          label: 'Somchai Jaidee',
          value: 'THB 4,523'
        }, {
          label: 'Nattaporn Srisuk',
          value: 'THB 3,845'
        }, {
          label: 'Prawit Chaiyasit',
          value: 'THB 2,987'
        }, {
          label: 'Siriporn Thongkham',
          value: 'THB 2,234'
        }, {
          label: 'Anong Wongsa',
          value: 'THB 1,892'
        }]} updateTime={getUpdateTime()} data-id="element-9711" />;
      case 'top-countries':
        return <ListWidget key={chartId} title="Top countries by spend" tooltipText="Estimated gross volume by country of the payment method used. The numbers include refunds and disputes." items={[{
          label: '🇺🇸 United States',
          value: 'THB 128,450'
        }, {
          label: '🇬🇧 United Kingdom',
          value: 'THB 87,320'
        }, {
          label: '🇩🇪 Germany',
          value: 'THB 64,180'
        }, {
          label: '🇦🇺 Australia',
          value: 'THB 52,740'
        }, {
          label: '🌐 Other',
          value: 'THB 123,330'
        }]} updateTime={getUpdateTime()} onViewMore={() => navigate('/payment-analytics?tab=payment-methods')} data-id="element-9712" />;
      case 'blocked-payments':
        return <MetricChart key={chartId} title="Blocked payments" tooltipText="Count of payments blocked due to risk settings" currentValue={totalBlockedCount.toString()} growthPercent={comparison !== 'none' ? 22.5 : undefined} previousValue={comparison !== 'none' ? '55' : undefined} data={blockedPaymentsData} previousData={comparison !== 'none' ? previousBlockedPaymentsData : []} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="blocked" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9713" />;
      case 'blocked-payments-volume':
        return <MetricChart key={chartId} title="Blocked payments volume" tooltipText="Volume of payments blocked due to risk settings" currentValue={formatCurrency(totalBlockedVolume)} growthPercent={comparison !== 'none' ? 18.2 : undefined} previousValue={comparison !== 'none' ? formatCurrency(totalBlockedVolume * 0.846) : undefined} data={blockedPaymentsVolumeData} previousData={comparison !== 'none' ? previousBlockedPaymentsVolumeData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `THB ${value.toLocaleString('en-US')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-9714" />;
      default:
        return null;
    }
  };
  return <div data-id="element-9715">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-9716">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-9717">Overview</h1>
      </div>

      {/* ================= QUICK OVERVIEW ================= */}
      <div className="mb-8" data-id="element-9718">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4" data-id="element-9719">
          <span className="text-lg" data-id="element-9720">🏠</span> Quick Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9721">
          <BalanceSnapshotWidget data-id="element-9722" />
          <AttentionNeededWidget data-id="element-9723" />
        </div>
      </div>

      {/* ================= ANALYTICS ================= */}
      {<div className="space-y-6" data-id="element-9724">
          {/* Analytics Header */}
          <div className="flex items-center justify-between" data-id="element-9725">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-9726">
              <span className="w-2.5 h-2.5 bg-blue-600 rounded-sm" data-id="element-9727"></span>{' '}
              Analytics
            </h2>
            <div className="flex items-center gap-2" data-id="element-9728">
              <button onClick={() => setShowAddChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9729">
                <PlusIcon data-id="element-9730" /> Add
              </button>
              <button onClick={() => setShowEditChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9731">
                <EditIcon data-id="element-9732" /> Edit
              </button>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-9733">
            <div className="flex flex-wrap items-center gap-2" data-id="element-9734">
              <DateRangePicker value={dateRange} onChange={handleDateRangeChange} data-id="element-9735" />
              <GranularitySelector value={granularity} onChange={handleGranularityChange} allowedGranularities={availableGranularities} hidden={isGranularityHidden} data-id="element-9736" />
              <ComparisonSelector value={comparison} onChange={setComparison} customRange={customComparisonRange} onCustomRangeChange={setCustomComparisonRange} data-id="element-9737" />
              <PaymentMethodFilter value={paymentMethods} onChange={setPaymentMethods} data-id="element-9738" />
            </div>
          </div>

          {/* Analytics Chart Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-9739">
            {chartOrder.filter(id => visibleCharts.has(id)).map(chartId => renderChart(chartId))}
          </div>
        </div>}

      {/* Modals */}
      {showAddChartModal && <AddChartModal onClose={() => setShowAddChartModal(false)} onAdd={ids => setVisibleCharts(new Set(ids))} initialSelectedCharts={visibleCharts} renderChart={renderChart} data-id="element-9740" />}
      {showEditChartModal && <EditChartsModal onClose={() => setShowEditChartModal(false)} onSave={(order, visible) => {
      setChartOrder(order);
      setVisibleCharts(visible);
    }} initialChartOrder={chartOrder} initialVisibleCharts={visibleCharts} chartData={new Map()} renderChart={renderChart} data-id="element-9741" />}
    </div>;
}
// Helper icons for buttons
function PlusIcon(props: any) {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" {...props} data-id="element-9742">
      <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" data-id="element-9743" />
    </svg>;
}
function EditIcon(props: any) {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" {...props} data-id="element-9744">
      <path d="M11.013 2.513a1.75 1.75 0 012.475 2.474l-8.61 8.61a.75.75 0 01-.353.194l-3.75.75a.75.75 0 01-.877-.877l.75-3.75a.75.75 0 01.194-.353l8.61-8.61z" data-id="element-9745" />
    </svg>;
}